import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Target, ListTodo, Info, Loader2, Edit3 } from 'lucide-react';
import { Dialog, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { goalService } from '@/lib/api/goal.service';
import type { GoalTemplate, GoalEntityType } from '@/types/goal.types';

interface ErrorResponse {
  response?: {
    data?: {
      message?: string;
    };
  };
}

interface GoalSelectionModalProps {
  open: boolean;
  onClose: () => void;
  entityType: GoalEntityType;
  onGoalSelected: (definitionId: number) => Promise<void>;
  attachedGoalIds?: number[];
  manualEntryUrl?: string;
}

export const GoalSelectionModal: React.FC<GoalSelectionModalProps> = ({
  open,
  onClose,
  entityType,
  onGoalSelected,
  attachedGoalIds = [],
  manualEntryUrl
}) => {
  const { t } = useTranslation('goals');
  const navigate = useNavigate();
  const [definitions, setDefinitions] = useState<GoalTemplate[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (open) {
      loadDefinitions();
    }
  }, [open, entityType]);

  const loadDefinitions = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await goalService.getDefinitionsByEntityType(entityType);
      // Filter out already attached goals
      const available = data.filter(d => !attachedGoalIds.includes(d.id));
      setDefinitions(available);
    } catch (err) {
      setError(t('selectionModal.errors.loadGoals'));
      console.error('Error loading goal definitions:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleSelect = async () => {
    if (!selectedId) return;
    
    try {
      setSubmitting(true);
      setError(null);
      await onGoalSelected(selectedId);
      onClose();
    } catch (err: unknown) {
      const message = (err as ErrorResponse)?.response?.data?.message || t('selectionModal.errors.addGoal');
      setError(message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose}>
      <DialogHeader>
        <DialogTitle>
          <span className="flex items-center gap-2">
            <Target className="h-5 w-5 text-primary" />
            {t('selectionModal.title')}
          </span>
        </DialogTitle>
      </DialogHeader>

      {manualEntryUrl && (
        <div className="px-4 pt-3">
          <Button
            variant="outline"
            size="sm"
            className="w-full"
            onClick={() => {
              onClose();
              navigate(manualEntryUrl);
            }}
          >
            <Edit3 className="h-3.5 w-3.5 mr-1.5" />
            {t('selectionModal.enterManually')}
          </Button>
        </div>
      )}

      <div className="mt-4 max-h-[400px] overflow-y-auto">
        {loading ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
          </div>
        ) : definitions.length === 0 ? (
          <div className="text-center py-8">
            <Target className="h-10 w-10 mx-auto text-muted-foreground/50 mb-2" />
            <p className="text-sm text-muted-foreground">{t('selectionModal.noGoals')}</p>
            <p className="text-xs text-muted-foreground mt-1">
              {t('selectionModal.noGoalsHint')}
            </p>
          </div>
        ) : (
          <div className="space-y-2">
            {definitions.map((def) => (
              <div
                key={def.id}
                className={`p-3 border rounded-lg cursor-pointer transition-colors ${
                  selectedId === def.id
                    ? 'border-primary bg-primary/5'
                    : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                }`}
                onClick={() => setSelectedId(def.id)}
              >
                <div className="flex items-start justify-between gap-2">
                  <div className="flex-1 min-w-0">
                    <h4 className="font-medium text-sm">{def.name}</h4>
                    {def.description && (
                      <p className="text-xs text-muted-foreground mt-0.5 line-clamp-2">
                        {def.description}
                      </p>
                    )}
                  </div>
                  <Badge variant="secondary" size="sm" className="flex-shrink-0">
                    <ListTodo className="h-3 w-3 mr-1" />
                    {t('selectionModal.tasksCount', { count: def.task_templates?.length || 0 })}
                  </Badge>
                </div>

                {(def.precondition_text || def.target_text) && (
                  <div className="mt-2 pt-2 border-t border-gray-100 space-y-1">
                    {def.precondition_text && (
                      <div className="flex items-start gap-1.5 text-xs">
                        <Info className="h-3 w-3 text-blue-500 mt-0.5 flex-shrink-0" />
                        <span className="text-muted-foreground">
                          <span className="font-medium">{t('selectionModal.precondition')}:</span> {def.precondition_text}
                        </span>
                      </div>
                    )}
                    {def.target_text && (
                      <div className="flex items-start gap-1.5 text-xs">
                        <Target className="h-3 w-3 text-green-500 mt-0.5 flex-shrink-0" />
                        <span className="text-muted-foreground">
                          <span className="font-medium">{t('selectionModal.target')}:</span> {def.target_text}
                        </span>
                      </div>
                    )}
                  </div>
                )}

                {selectedId === def.id && def.task_templates && def.task_templates.length > 0 && (
                  <div className="mt-2 pt-2 border-t border-gray-100">
                    <p className="text-xs font-medium text-muted-foreground mb-1">{t('selectionModal.tasksToBeCreated')}</p>
                    <ul className="space-y-0.5">
                      {def.task_templates.map((template, idx) => (
                        <li key={template.id} className="text-xs text-muted-foreground flex items-center gap-1">
                          <span className="w-4 text-center text-muted-foreground/50">{idx + 1}.</span>
                          {template.task_title || template.task_description || t('selectionModal.taskFallback', { index: idx + 1 })}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}

        {error && (
          <div className="mt-3 p-2 bg-red-50 border border-red-200 rounded text-red-600 text-xs">
            {error}
          </div>
        )}
      </div>

      <DialogFooter>
        <Button variant="outline" onClick={onClose} disabled={submitting}>
          {t('common:actions.cancel')}
        </Button>
        <Button
          onClick={handleSelect}
          disabled={!selectedId || submitting}
        >
          {submitting ? (
            <>
              <Loader2 className="h-4 w-4 mr-1 animate-spin" />
              {t('selectionModal.adding')}
            </>
          ) : (
            t('selectionModal.addGoal')
          )}
        </Button>
      </DialogFooter>
    </Dialog>
  );
};

export default GoalSelectionModal;
